import type { FC, ReactNode, RefObject } from 'react'
import type { Work } from '@/types'

import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react'
import { ArrowUpRight, X } from 'lucide-react'
import { cn } from '@/lib/utils'

import {
    getPlayerUrl,
    getProviderName,
    getWatchUrl,
    parseAspect,
    parseVideoUrl
} from '@/lib/video'

interface FilmPlayerContextValue {
    film: Work | null
    openFilm: (work: Work) => void
    closeFilm: () => void
}

interface FilmPlayerProviderProps {
    children: ReactNode
}

interface FilmLightboxProps {
    work: Work
    playing: boolean
    visible: boolean
    onClose: () => void
    closeButtonRef: RefObject<HTMLButtonElement | null>
}

const EXIT_MS = 450
const BLOCKED_KEYS = ['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' ']

const FilmPlayerContext = createContext<FilmPlayerContextValue>({
    film: null,
    openFilm: () => undefined,
    closeFilm: () => undefined
})

export const useFilmPlayer = (): FilmPlayerContextValue => useContext(FilmPlayerContext)

const FilmLightbox: FC<FilmLightboxProps> = ({ work, playing, visible, onClose, closeButtonRef }): ReactNode => {
    const source = parseVideoUrl(work.videoUrl)
    const ratio = parseAspect(work.aspect)

    return (
        <div
            role='dialog'
            aria-modal='true'
            aria-label={`${work.client}, ${work.title}`}
            className={cn(
                'fixed inset-0 z-[100] flex flex-col bg-ink/95 text-bone backdrop-blur-xl transition-opacity duration-500 ease-out',
                visible ? 'opacity-100' : 'pointer-events-none opacity-0'
            )}
        >
            <div aria-hidden className='film-grain pointer-events-none absolute inset-0 opacity-[0.05]' />

            <div className='relative z-10 flex items-start justify-between gap-6 px-[var(--site-gutter)] pt-6 md:pt-8'>
                <div className='min-w-0'>
                    <p className='text-[10px] font-medium uppercase tracking-[0.36em] text-white/50'>
                        Now playing
                    </p>

                    <p className='mt-2 font-heading text-[clamp(1.25rem,2.4vw,2rem)] leading-tight text-bone'>
                        <span data-mdlfy-cms={`works:${work.id}:client`}>{work.client}</span>
                        <span aria-hidden className='mx-3 text-white/30'>/</span>
                        <span className='italic' data-mdlfy-cms={`works:${work.id}:title`}>{work.title}</span>
                    </p>
                </div>

                <button
                    ref={closeButtonRef}
                    type='button'
                    onClick={onClose}
                    aria-label='Close film'
                    className='flex size-12 shrink-0 cursor-pointer items-center justify-center rounded-full border border-white/30 bg-white/[0.04] text-white/85 transition-colors duration-300 hover:border-white/80 hover:bg-white/10 hover:text-white'
                >
                    <X className='size-5' strokeWidth={1.4} />
                </button>
            </div>

            <div
                className='relative z-10 flex min-h-0 flex-1 items-center justify-center px-[var(--site-gutter)] py-6'
                onClick={(event) => {
                    if (event.target === event.currentTarget) onClose()
                }}
            >
                <div
                    className='relative overflow-hidden rounded-[20px] bg-black shadow-[0_40px_120px_-30px_rgba(0,0,0,0.9)] md:rounded-[28px]'
                    style={{
                        aspectRatio: ratio,
                        width: `min(100%, calc((100dvh - 220px) * ${ratio.toFixed(4)}))`
                    }}
                >
                    {playing && source && (
                        <iframe
                            src={getPlayerUrl(source)}
                            title={`${work.client}, ${work.title}`}
                            allow='autoplay; fullscreen; picture-in-picture; encrypted-media'
                            allowFullScreen
                            className='absolute inset-0 size-full'
                            style={{ border: 0 }}
                        />
                    )}

                    {!source && (
                        <div className='absolute inset-0 flex items-center justify-center p-8 text-center text-sm text-white/60'>
                            This film is not available to play right now.
                        </div>
                    )}
                </div>
            </div>

            <div className='relative z-10 flex flex-wrap items-center justify-between gap-4 px-[var(--site-gutter)] pb-6 text-[10.5px] font-medium uppercase tracking-[0.32em] text-white/55 md:pb-8'>
                <div className='flex flex-wrap items-center gap-x-4 gap-y-1'>
                    {work.role && (
                        <span className='text-white/80' data-mdlfy-cms={`works:${work.id}:role`}>{work.role}</span>
                    )}

                    {work.year && (
                        <>
                            <span aria-hidden className='text-white/25'>/</span>
                            <span data-mdlfy-cms={`works:${work.id}:year`}>{work.year}</span>
                        </>
                    )}
                </div>

                {source && (
                    <a
                        href={getWatchUrl(source)}
                        target='_blank'
                        rel='noreferrer'
                        className='inline-flex items-center gap-2 text-white/70 transition-colors hover:text-white'
                    >
                        Watch on {getProviderName(source)}
                        <ArrowUpRight className='size-4' strokeWidth={1.5} />
                    </a>
                )}
            </div>
        </div>
    )
}

export const FilmPlayerProvider: FC<FilmPlayerProviderProps> = ({ children }): ReactNode => {
    const [film, setFilm] = useState<Work | null>(null)
    const [shown, setShown] = useState<Work | null>(null)
    const [visible, setVisible] = useState(false)
    const closeButtonRef = useRef<HTMLButtonElement | null>(null)
    const returnFocusRef = useRef<HTMLElement | null>(null)
    const exitTimerRef = useRef<number | null>(null)

    const openFilm = useCallback((work: Work) => {
        if (exitTimerRef.current) window.clearTimeout(exitTimerRef.current)

        returnFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null
        setShown(work)
        setFilm(work)
        window.requestAnimationFrame(() => setVisible(true))
    }, [])

    const closeFilm = useCallback(() => {
        setFilm(null)
        setVisible(false)

        if (exitTimerRef.current) window.clearTimeout(exitTimerRef.current)

        exitTimerRef.current = window.setTimeout(() => setShown(null), EXIT_MS)
    }, [])

    useEffect(() => {
        if (!film) return

        const root = document.documentElement
        const previousOverflow = root.style.overflow

        root.style.overflow = 'hidden'
        closeButtonRef.current?.focus({ preventScroll: true })

        const onKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                event.preventDefault()
                event.stopPropagation()
                closeFilm()
                return
            }

            if (BLOCKED_KEYS.includes(event.key)) event.stopPropagation()
        }

        window.addEventListener('keydown', onKey, true)

        return () => {
            window.removeEventListener('keydown', onKey, true)
            root.style.overflow = previousOverflow
            returnFocusRef.current?.focus({ preventScroll: true })
        }
    }, [film, closeFilm])

    useEffect(() => () => {
        if (exitTimerRef.current) window.clearTimeout(exitTimerRef.current)
    }, [])

    const value = useMemo(() => ({ film, openFilm, closeFilm }), [film, openFilm, closeFilm])

    return (
        <FilmPlayerContext.Provider value={value}>
            <div className='contents' inert={film !== null}>
                {children}
            </div>

            {shown && (
                <FilmLightbox
                    work={shown}
                    playing={film !== null}
                    visible={visible}
                    onClose={closeFilm}
                    closeButtonRef={closeButtonRef}
                />
            )}
        </FilmPlayerContext.Provider>
    )
}
